import React, { useState } from 'react'
import { Bar, Doughnut } from 'react-chartjs-2'
import {
  Button,
  Spinner,
  Row,
  Col,
  InputGroup,
  Form,
  DropdownButton,
  Dropdown,
  Table,
} from 'react-bootstrap'
import {
  FaSearch,
  FaSort,
  FaExchangeAlt,
  FaChartBar,
  FaChartPie,
  FaCheckCircle,
  FaArrowCircleDown,
  FaArrowCircleUp,
} from 'react-icons/fa'
import { ChartData, ChartOptions } from 'chart.js'
import {
  StatsSection,
  SectionTitle,
  DetailCard,
  DetailHeader,
  ChartContainer,
  FlipCard,
} from '../styles'

interface ClassDistributionItem {
  id: string
  name: string
  value: number
  percentage: number
  color: string
  balance: string[]
}

interface ClassDistributionSectionProps {
  classDistribution: ClassDistributionItem[]
  isLoading: boolean
  totalImages: number
  barChartData: ChartData<'bar'>
  barChartOptions: ChartOptions<'bar'>
  pieChartData: ChartData<'doughnut'>
  pieChartOptions: ChartOptions<'doughnut'>
}

export const ClassDistributionSection: React.FC<ClassDistributionSectionProps> = ({
  classDistribution,
  isLoading,
  totalImages,
  barChartData,
  barChartOptions,
  pieChartData,
  pieChartOptions,
}) => {
  const [isFlipped, setIsFlipped] = useState(true)
  const [chartViewMode, setChartViewMode] = useState<'bar' | 'doughnut'>('bar')
  const [classNameSearch, setClassNameSearch] = useState('')
  const [sortBy, setSortBy] = useState<'value' | 'percentage' | 'name'>('value')
  const [filter, setFilter] = useState<'all' | 'bal' | 'over' | 'under'>('all')

  // Filter and sort the class distribution data
  const filteredClasses = classDistribution.filter((item) => {
    const matchesSearch = item.name.toLowerCase().includes(classNameSearch.toLowerCase())
    const matchesFilter = filter === 'all' || item.balance.includes(filter)
    return matchesSearch && matchesFilter
  })

  const sortedClasses = [...filteredClasses].sort((a, b) => {
    if (sortBy === 'value') return b.value - a.value
    if (sortBy === 'name') return a.name.localeCompare(b.name)
    if (sortBy === 'percentage') return b.percentage - a.percentage
    return 0
  })

  return (
    <StatsSection>
      <SectionTitle>
        <FaChartBar /> Class Distribution
      </SectionTitle>
      <FlipCard className={`flip-card ${isFlipped ? 'flipped' : ''}`}>
        <div className="flip-card-inner">
          <div className="flip-card-front">
            <DetailCard>
              <DetailHeader>
                <div>
                  <h3>Distribution Chart</h3>
                </div>
                <div className="d-flex gap-2">
                  <div className="chart-type-buttons d-flex gap-1">
                    <Button
                      size="sm"
                      variant={chartViewMode === 'bar' ? 'primary' : 'outline-light'}
                      onClick={() => setChartViewMode('bar')}
                      title="Bar Chart"
                      className="chart-type-btn"
                    >
                      <FaChartBar />
                    </Button>
                    <Button
                      size="sm"
                      variant={chartViewMode === 'doughnut' ? 'primary' : 'outline-light'}
                      onClick={() => setChartViewMode('doughnut')}
                      title="Doughnut Chart"
                      className="chart-type-btn"
                    >
                      <FaChartPie />
                    </Button>
                  </div>
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => setIsFlipped(!isFlipped)}
                    className="flip-button"
                  >
                    <FaExchangeAlt className="me-2" />
                    Show Table
                  </Button>
                </div>
              </DetailHeader>
              <DetailCard.Body>
                <ChartContainer>
                  {isLoading ? (
                    <div className="loading-overlay">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      {chartViewMode === 'bar' && (
                        <Bar data={barChartData} options={barChartOptions} />
                      )}
                      {chartViewMode === 'doughnut' && (
                        <Doughnut data={pieChartData} options={pieChartOptions} />
                      )}
                    </>
                  )}
                </ChartContainer>
              </DetailCard.Body>
            </DetailCard>
          </div>
          <div className="flip-card-back">
            <DetailCard>
              <DetailHeader>
                <div>
                  <h3>Distribution Table</h3>
                </div>
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={() => setIsFlipped(!isFlipped)}
                  className="flip-button"
                >
                  <FaExchangeAlt className="me-2" />
                  Show Chart
                </Button>
              </DetailHeader>
              <DetailCard.Body>
                <div className="filters-section">
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <InputGroup>
                        <InputGroup.Text className="bg-dark border-secondary">
                          <FaSearch />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search classes..."
                          className="bg-dark border-secondary text-light"
                          value={classNameSearch}
                          onChange={(e) => setClassNameSearch(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col xs={12} md={3}>
                      <DropdownButton
                        variant="outline-secondary"
                        title={`Sort: ${sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}`}
                        className="w-100"
                      >
                        <Dropdown.Item onClick={() => setSortBy('value')}>
                          <FaSort className="me-2" />
                          Count
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setSortBy('percentage')}>
                          <FaSort className="me-2" />
                          Percentage
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setSortBy('name')}>
                          <FaSort className="me-2" />
                          Name
                        </Dropdown.Item>
                      </DropdownButton>
                    </Col>
                    <Col xs={12} md={3}>
                      <DropdownButton
                        id="filter-dropdown"
                        variant="outline-secondary"
                        title={`Filter: ${filter.charAt(0).toUpperCase() + filter.slice(1)}`}
                        className="w-100"
                      >
                        <Dropdown.Item onClick={() => setFilter('all')}>All Classes</Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilter('bal')}>Balanced</Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilter('over')}>
                          Overrepresented
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setFilter('under')}>
                          Underrepresented
                        </Dropdown.Item>
                      </DropdownButton>
                    </Col>
                  </Row>
                </div>
                <div className="class-list-container">
                  <Table hover variant="dark" className="class-table">
                    <thead>
                      <tr>
                        <th>Class Name</th>
                        <th className="text-center">
                          {sortBy === 'percentage' ? 'Percentage' : 'Count'}
                        </th>
                        <th className="text-center">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={3} className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                          </td>
                        </tr>
                      ) : (
                        sortedClasses.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div
                                  className="class-color-indicator"
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: item.color,
                                    marginRight: '12px',
                                    flexShrink: 0,
                                  }}
                                />
                                <span className="class-name">{item.name}</span>
                              </div>
                            </td>
                            <td className="text-center">
                              {sortBy === 'percentage'
                                ? `${((item.value / totalImages) * 100).toFixed(1)}%`
                                : item.value.toLocaleString()}
                            </td>
                            <td className="text-center balance-column">
                              {item.balance.includes('balanced') && (
                                <span className="text-success">
                                  <FaCheckCircle className="me-2" />
                                  Balanced
                                </span>
                              )}
                              {item.balance.includes('under') && (
                                <span className="text-danger">
                                  <FaArrowCircleDown className="me-2" />
                                  Underrepresented
                                </span>
                              )}
                              {item.balance.includes('over') && (
                                <span className="text-warning">
                                  <FaArrowCircleUp className="me-2" />
                                  Overrepresented
                                </span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </DetailCard.Body>
            </DetailCard>
          </div>
        </div>
      </FlipCard>
    </StatsSection>
  )
}
